import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ExpandableUserSelector from '../../components/ExpandableUserSelector';
import { authHeaders, guidesFetch } from './guidesUtils';
import { flashcardsFetch } from './flashcards/flashcardsUtils';

const SHARE_PATHS = {
  article: (id) => ({ fetch: guidesFetch, path: `/articles/${id}/share` }),
  notebook: (id) => ({ fetch: guidesFetch, path: `/notebooks/${id}/share` }),
  flashcard_deck: (id) => ({ fetch: flashcardsFetch, path: `/decks/${id}/share` }),
  flashcard_set: (id) => ({ fetch: flashcardsFetch, path: `/sets/${id}/share` })
};

export default function ShareGuideModal({ show, onHide, targetType, targetId, initialUserIds = [], onSaved }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSelected((initialUserIds || []).map(Number));
    setLoading(true);
    guidesFetch('/program-users', { headers: authHeaders() })
      .then(setUsers)
      .catch((err) => toast.error(err.message || 'Failed to load users'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, targetType, targetId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const resolver = SHARE_PATHS[targetType] || SHARE_PATHS.notebook;
      const { fetch: doFetch, path } = resolver(targetId);
      const shares = await doFetch(path, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ userIds: selected })
      });
      toast.success('Sharing updated');
      onSaved?.(shares);
      onHide();
    } catch (err) {
      toast.error(err.message || 'Failed to share');
    } finally {
      setSaving(false);
    }
  };

  const helperText = targetType?.startsWith('flashcard')
    ? 'Selected users will be able to view and study these flashcards.'
    : 'Selected users will be able to view this guide.';

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton className="border-0">
        <div className="d-flex flex-column align-items-start w-100">
          <Modal.Title>Share with program users</Modal.Title>
          <div className="text-hl"></div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="guides-loading"><Spinner /></div>
        ) : (
          <ExpandableUserSelector
            availableUsers={users}
            selectedUsers={selected}
            onSelectionChange={setSelected}
            helperText={helperText}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || loading}>
          {saving ? <Spinner size="sm" /> : 'Save sharing'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
